import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Truck, Award, IndianRupee, Star, ChevronRight,
  Smartphone, Tv, Headphones, Refrigerator, Zap, ChevronLeft,
  Phone, MapPin, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ProductCard from '@/components/ProductCard';
import CustomerReviewsSection from '@/components/CustomerReviewsSection';
import { useFeaturedProducts, useFlashSaleProducts, useTopRatedProducts, useOnSaleProducts } from '@/hooks/useProducts';
import { useHeroImage, useHeroSlides } from '@/hooks/useHeroImage';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { brands } from '@/data/products';
import heroBanner from '@/assets/hero-banner.jpg';

const trustBadges = [
  { icon: Shield, label: '100% Genuine', desc: 'Authorized dealer products', color: 'text-blue-500' },
  { icon: Truck, label: 'Fast Delivery', desc: 'Same day dispatch', color: 'text-green-500' },
  { icon: Award, label: 'Full Warranty', desc: 'Manufacturer warranty', color: 'text-yellow-500' },
  { icon: IndianRupee, label: 'Best Prices', desc: 'Price match guarantee', color: 'text-purple-500' },
];

const categoryCards = [
  { id: 'smartphones', name: 'Smartphones', icon: Smartphone, gradient: 'from-blue-600 to-cyan-500', shadow: 'shadow-blue-200' },
  { id: 'led-tvs', name: 'LED TVs', icon: Tv, gradient: 'from-violet-600 to-purple-500', shadow: 'shadow-violet-200' },
  { id: 'accessories', name: 'Accessories', icon: Headphones, gradient: 'from-orange-500 to-yellow-400', shadow: 'shadow-orange-200' },
  { id: 'appliances', name: 'Appliances', icon: Refrigerator, gradient: 'from-emerald-600 to-teal-500', shadow: 'shadow-emerald-200' },
];

const faqs = [
  { q: "Are all products genuine?", a: "Yes! We are authorized dealers and sell 100% genuine products with manufacturer warranty and proper GST bills." },
  { q: "Do you offer EMI options?", a: "Yes, we offer easy EMI options on credit cards and select debit cards. Contact us for details." },
  { q: "What is your return policy?", a: "We offer 7-day replacement for manufacturing defects. Products must be in original condition with packaging." },
  { q: "Do you deliver outside the city?", a: "Yes, we deliver across India via trusted courier partners. Delivery charges may apply based on location." },
];

const promoSlides = [
  { title: 'vivo T5x 5G', subtitle: 'New Arrival — Now Available', tag: 'Starting ₹18,999', bg: 'from-blue-950 via-slate-900 to-blue-950', link: '/products?search=vivo+T5x' },
  { title: 'vivo V70 Series', subtitle: '200MP Camera • 7000mAh Battery', tag: 'Starting ₹37,999', bg: 'from-purple-950 via-slate-900 to-purple-950', link: '/products?search=vivo+V70' },
  { title: 'vivo X200T', subtitle: 'Flagship Performance', tag: 'Starting ₹59,999', bg: 'from-emerald-950 via-slate-900 to-emerald-950', link: '/products?search=vivo+X200T' },
];

const PromoSlider = () => {
  const [current, setCurrent] = useState(0);

  const { data: dbSlides = [] } = useQuery({
    queryKey: ['promo-slides'],
    queryFn: async () => {
      const { data } = await (supabase as any).from('promo_slides').select('*').eq('is_active', true).order('sort_order');
      return data || [];
    },
  });

  const slides: any[] = dbSlides.length > 0 ? dbSlides : promoSlides;

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setCurrent(p => (p + 1) % slides.length), 3500);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const slide = slides[current];

  return (
    <section className="container px-4 py-8">
      <div className={`relative rounded-3xl overflow-hidden bg-gradient-to-r ${slide.bg} h-[400px]`}>
        {/* Product Image - Centered at 70% from left */}
        {slide.image_url && (
          <div className="absolute left-[75%] -translate-x-1/2 top-1/2 -translate-y-1/2 w-[45%] h-[90%] flex items-center justify-center">
            <img 
              src={slide.image_url} 
              alt={slide.title} 
              className="h-full w-full object-contain" 
            />
          </div>
        )}
        {/* Decorative circles */}
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute -right-8 -bottom-8 w-48 h-48 rounded-full bg-white/5" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent pointer-events-none" />

        {/* Text Content - Left side */}
        <div className="absolute left-8 md:left-16 top-1/2 -translate-y-1/2 z-10 w-[45%]">
          <motion.div key={current} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="text-white">
            <span className="text-xs font-semibold bg-white/20 backdrop-blur-sm border border-white/20 px-3 py-1 rounded-full mb-3 inline-block">{slide.tag}</span>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold mb-1 drop-shadow-lg">{slide.title}</h2>
            <p className="text-sm opacity-75 mb-5">{slide.subtitle}</p>
            <Link to={slide.link}>
              <Button size="sm" className="bg-white text-slate-900 hover:bg-white/90 font-semibold rounded-full px-5">View Details →</Button>
            </Link>
          </motion.div>
        </div>

        <button onClick={() => setCurrent(p => (p - 1 + slides.length) % slides.length)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/25 backdrop-blur-sm text-white rounded-full p-2 transition-all border border-white/20">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button onClick={() => setCurrent(p => (p + 1) % slides.length)}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/25 backdrop-blur-sm text-white rounded-full p-2 transition-all border border-white/20">
          <ChevronRight className="h-4 w-4" />
        </button>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'bg-white w-8' : 'bg-white/40 w-1.5'}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

const CountdownTimer = () => {
  const [time, setTime] = useState({ h: 5, m: 23, s: 45 });
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(prev => {
        let { h, m, s } = prev;
        s--;
        if (s < 0) { s = 59; m--; }
        if (m < 0) { m = 59; h--; }
        if (h < 0) { h = 23; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-1.5">
      {[['h', time.h, 'HRS'], ['m', time.m, 'MIN'], ['s', time.s, 'SEC']].map(([key, val, lbl]) => (
        <div key={key as string} className="bg-destructive text-white rounded-lg px-2.5 py-1.5 text-center min-w-[46px]">
          <p className="font-heading font-bold text-base leading-none">{String(val).padStart(2, '0')}</p>
          <p className="text-[8px] uppercase opacity-80 mt-0.5">{lbl as string}</p>
        </div>
      ))}
    </div>
  );
};

const HomePage = () => {
  const { data: featuredProducts = [] } = useFeaturedProducts();
  const { data: flashSaleProducts = [] } = useFlashSaleProducts();
  const { data: topRatedProducts = [] } = useTopRatedProducts();
  const { data: onSaleProducts = [] } = useOnSaleProducts();
  const { data: heroImageUrl } = useHeroImage();
  const { data: heroSlides = [] } = useHeroSlides();
  const [productTab, setProductTab] = useState<'featured' | 'sale' | 'top'>('featured');
  const [heroIndex, setHeroIndex] = useState(0);
  const [prevHeroIndex, setPrevHeroIndex] = useState<number | null>(null);

  // Build slides array: only use uploaded slides, no fallback image
  const heroImages = heroSlides.filter(Boolean);

  // Auto-advance every 4 seconds
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const t = setInterval(() => {
      setHeroIndex(p => {
        setPrevHeroIndex(p);
        return (p + 1) % heroImages.length;
      });
    }, 4000);
    return () => clearInterval(t);
  }, [heroImages.length]);

  return (
    <div className="bg-background">

      {/* ── HERO ── */}
      <section className="relative overflow-hidden min-h-[480px] flex flex-col justify-between bg-slate-900 text-white">
        {/* Sliding background images - only shown when uploaded via admin */}
        {heroImages.filter(img => img && img !== heroBanner).map((img, i) => {
          const isActive = i === heroIndex;
          const isLeaving = i === prevHeroIndex;
          if (!isActive && !isLeaving) return null;
          return (
            <img
              key={img}
              src={img}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover object-center ${
                isActive ? 'animate-burn-in z-[2]' : 'animate-burn-out z-[1]'
              }`}
            />
          );
        })}

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/30 to-transparent" />

        {/* Content */}
        <div className="relative z-10 container pt-10 pb-6 flex-1 flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-md">
            {/* Logo + Name */}
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shrink-0">
                <img src="/logo.png" alt="Debnath Telecom" className="h-11 w-11 object-contain" />
              </div>
              <div>
                <p className="font-heading font-bold text-white text-base leading-tight">Debnath Telecom</p>
                <p className="text-cyan-400 text-xs">& Variety Store</p>
              </div>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl font-extrabold leading-tight mb-4">
              Your Trusted<br />
              <span className="text-cyan-400">Mobile & Electronics</span><br />
              Store
            </h1>
            <p className="text-sm text-white/75 mb-8 leading-relaxed max-w-sm">
              Discover the latest smartphones, TVs, and accessories from top brands. Quality products with warranty and unbeatable prices.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/products">
                <Button className="bg-white text-slate-900 hover:bg-white/90 font-semibold rounded-full px-6 gap-2">
                  Explore Products <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/deals">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 font-semibold">
                  View Deals
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Slide arrows + dots — only when multiple slides */}
        {heroImages.length > 1 && (
          <>
            <button onClick={() => { setPrevHeroIndex(heroIndex); setHeroIndex(p => (p - 1 + heroImages.length) % heroImages.length); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => { setPrevHeroIndex(heroIndex); setHeroIndex(p => (p + 1) % heroImages.length); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition-colors">
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {heroImages.map((_, i) => (
                <button key={i} onClick={() => { setPrevHeroIndex(heroIndex); setHeroIndex(i); }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === heroIndex ? 'bg-white w-8' : 'bg-white/40 w-1.5'}`} />
              ))}
            </div>
          </>
        )}

        {/* Trust badges bar at bottom of hero */}
        <div className="relative z-10 border-t border-white/10 bg-black/30 backdrop-blur-sm">
          <div className="container py-3">
            <div className="flex items-center justify-start gap-10">
              {[
                { icon: Truck, label: 'Fast Delivery' },
                { icon: Shield, label: 'Warranty' },
                { icon: IndianRupee, label: 'Best Prices' },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-2 text-white/80">
                  <b.icon className="h-4 w-4 text-cyan-400" />
                  <span className="text-xs font-medium">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FLASH SALE ── */}
      {flashSaleProducts.length > 0 && (
        <section className="section-padding">
          <div className="container">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-2xl font-bold">Flash Sale</h2>
                    <span className="text-[10px] font-bold bg-destructive text-white px-2 py-0.5 rounded-full animate-pulse">LIVE</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Deals rotate every hour</p>
                </div>
              </div>
              <CountdownTimer />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {flashSaleProducts.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── PROMO SLIDER ── */}
      <PromoSlider />

      {/* ── CATEGORIES ── */}
      <section className="section-padding bg-muted/30">
        <div className="container">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Browse</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold">Shop by Category</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categoryCards.map((cat, i) => (
              <motion.div key={cat.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to={`/products?category=${cat.id}`}
                  className={`group flex flex-col items-center justify-center p-8 rounded-2xl bg-gradient-to-br ${cat.gradient} text-white shadow-lg ${cat.shadow} hover:scale-105 hover:shadow-xl transition-all duration-300`}>
                  <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center mb-4 group-hover:bg-white/30 transition-colors">
                    <cat.icon className="h-7 w-7" />
                  </div>
                  <p className="font-heading font-bold text-base">{cat.name}</p>
                  <p className="text-xs opacity-70 mt-1">Shop now →</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BRAND MARQUEE ── */}
      <section className="py-6 border-y bg-card overflow-hidden">
        <div className="flex animate-marquee gap-16 items-center">
          {[...brands, ...brands].map((brand, i) => (
            <span key={`${brand}-${i}`} className="text-muted-foreground/40 font-heading font-bold text-lg whitespace-nowrap">{brand}</span>
          ))}
        </div>
      </section>

      {/* ── FEATURED / ON SALE / TOP RATED TABS ── */}
      <section className="section-padding">
        <div className="container">
          {/* Tab header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex bg-muted rounded-xl p-1 gap-1 w-fit">
              {([
                { key: 'featured', label: '⭐ Featured' },
                { key: 'sale', label: '🏷️ On Sale' },
                { key: 'top', label: '🔥 Top Rated' },
              ] as const).map(t => (
                <button
                  key={t.key}
                  onClick={() => setProductTab(t.key)}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                    productTab === t.key
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <Link to="/products">
              <Button variant="outline" size="sm" className="rounded-full gap-1 font-semibold">
                View All <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>

          {/* Tab content */}
          {productTab === 'featured' && (
            featuredProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {featuredProducts.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p>No featured products yet. Mark products as featured from the admin panel.</p>
              </div>
            )
          )}

          {productTab === 'sale' && (
            onSaleProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {onSaleProducts.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p>No products on sale right now.</p>
              </div>
            )
          )}

          {productTab === 'top' && (
            topRatedProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {topRatedProducts.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p>No rated products yet.</p>
              </div>
            )
          )}
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="section-padding bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: '10+', label: 'Years in Business', icon: Clock },
              { num: '5000+', label: 'Happy Customers', icon: Star },
              { num: '20+', label: 'Brand Partners', icon: Award },
              { num: '100%', label: 'Genuine Products', icon: Shield },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="h-6 w-6 text-cyan-400" />
                </div>
                <p className="font-heading text-3xl font-extrabold text-cyan-400">{stat.num}</p>
                <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <CustomerReviewsSection />

      {/* ── FAQ ── */}
      <section className="section-padding">
        <div className="container max-w-2xl">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Help</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-card border rounded-2xl px-5 shadow-sm">
                <AccordionTrigger className="font-heading font-semibold text-sm py-4">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section-padding bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">Ready to Shop?</h2>
              <p className="opacity-80 max-w-md">Visit our store or shop online. We deliver across India with full warranty and genuine products guarantee.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link to="/products">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-white/90 font-heading font-bold rounded-full px-7">Browse Products</Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading font-semibold rounded-full px-7">
                  <Phone className="h-4 w-4 mr-2" /> Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
